// 03 · PAD — l'étape la plus retravaillée (§7.3). Quatre issues, deux natures.
//
//   Branche A — j'ai le justificatif
//     carte  → télécollectée ?     oui = conforme · non = anomalie (montant réel)
//     chèque → montant correct ?   oui = conforme · non = anomalie (montant réel)
//   Branche B — je n'ai pas le justificatif
//     carte télécollectée ?        oui = conforme, dossier clos
//                                  non = PAD EN ATTENTE, qui poursuit les jours suivants
//
// Asymétrie fondamentale : un justificatif présent mais anormal EXPLIQUE l'écart
// de flux du jour, il ne se reporte pas. Un justificatif absent et non couvert
// par une télécollecte est un dossier ouvert.
//
// Le montant réel fait foi : c'est lui qui part dans les totaux cartes et
// chèques de l'étape 11, puisque c'est l'argent réellement encaissé. Sans cela
// les totaux COL MNC seraient faux.
//
// Les quatre issues sont à DEUX clics : la branche, puis l'issue. Les deux
// boutons de branche sont un sélecteur d'état (`aria-pressed`) — recliquer la
// branche ouverte la referme, il n'y a donc pas de bouton « retour » à viser.

import { useState } from 'react'
import { useRun } from '@/data/RunProvider'
import * as api from '@/data/api'
import { etatPad, type LignePad } from '@/domain/derive'
import { couleurEcart, fmtEcart, lampeEcart, motEcart, severite } from '@/domain/ecart'
import { fmtEur, formatShort, pluriel } from '@/lib/format'
import { messageErreur } from '@/lib/errors'
import { Card, Chip, Kpis, Lamp, Notice, Modale, Vide } from '@/ui/primitives'
import { useToast } from '@/ui/Toast'
import type { PadAttente, PadVerif } from '@/domain/types'
import type { PropsEtape } from './types'

type Issue = 'carte_ok' | 'carte_ko' | 'cheque_ok' | 'cheque_ko' | 'absent_ok' | 'absent_ko'

/** Le montant réellement reçu, ou `null` s'il n'a jamais été saisi. */
const montantReel = (l: LignePad): number | null =>
  typeof l.verif?.montant_reel === 'number' ? l.verif.montant_reel : null

/**
 * Lecture d'un montant tapé à la main : virgule ou point, espaces d'affichage
 * et symbole € tolérés — un copier-coller depuis ORIKA arrive en « 1 234,56 € »,
 * espace fine insécable comprise, et se faire renvoyer « montant invalide »
 * pour un caractère qu'on n'a pas tapé soi-même coûte une ressaisie complète.
 * Retourne `null` sur une saisie vide ou impossible — un champ laissé vide ne
 * doit pas devenir « 0,00 € reçu » sans que personne ne l'ait décidé.
 */
function montantSaisi(texte: string): number | null {
  const brut = texte
    .replace(/[\s\u00a0\u202f]/g, '')
    .replace(/€|eur/gi, '')
    .replace(',', '.')
  if (!brut) return null
  const n = Number(brut)
  if (!Number.isFinite(n) || n < 0) return null
  return +n.toFixed(2)
}

export default function Pad({ rapport, lectureSeule }: PropsEtape) {
  const { date, padAttente, padResoudre, padAjouter, majRapport } = useRun()
  const toast = useToast()
  const [saisie, setSaisie] = useState<{ ligne: LignePad; issue: Issue } | null>(null)
  const [montant, setMontant] = useState('')
  const [renoncement, setRenoncement] = useState<PadAttente | null>(null)
  const [motif, setMotif] = useState('')
  // Clé de l'écriture en vol. Les écritures sont sérialisées — chaque appel
  // renvoie la table des vérifications ENTIÈRE, deux réponses concurrentes
  // s'écrasent l'une l'autre — donc une seule à la fois, et toutes les commandes
  // qui écrivent portent `data-encours` pendant ce temps.
  const [occupe, setOccupe] = useState<string | null>(null)

  const { lignes, statues, ecartTotal, total } = etatPad(rapport)
  const enAttente = padAttente.filter((p) => p.statut === 'en_attente')

  /** Une écriture est en vol : TOUTES les commandes de l'écran sont inertes. */
  const busy = occupe !== null
  const encours = busy ? 'true' : undefined

  /**
   * Le dossier d'attente ouvert par CETTE ligne, aujourd'hui. Les dossiers
   * reportés des journées précédentes appartiennent à la ligne d'un autre jour :
   * on n'y touche pas depuis ici.
   *
   * L'identité passe par `payment_id` dès qu'il existe. La référence ne vient
   * qu'après : ORIKA n'en fournit pas pour les PAD, `refPad` retombe sur le
   * couple caisse / ticket, et deux lignes du même jour peuvent donc porter la
   * même — statuer l'une refermait alors le dossier de l'autre.
   */
  const dossierDuJour = (ligne: LignePad): PadAttente | undefined =>
    enAttente.find((p) => p.journee_origine === date
      && (p.payment_id ? p.payment_id === ligne.id : p.pad_ref === ligne.ref))

  /** Nombre de jours pendant lesquels le dossier a déjà été reporté. */
  const joursOuvert = (origine: string): number => {
    const a = Date.parse(`${origine}T00:00:00Z`)
    const b = Date.parse(`${date}T00:00:00Z`)
    if (!Number.isFinite(a) || !Number.isFinite(b)) return 0
    return Math.max(0, Math.round((b - a) / 86_400_000))
  }

  /**
   * Statuer une ligne. Retourne `true` si l'écriture est passée — l'appelant
   * s'en sert pour ne fermer la modale que sur un succès.
   */
  const statuer = async (ligne: LignePad, issue: Issue, reel?: number): Promise<boolean> => {
    if (lectureSeule || occupe) return false

    const base: PadVerif = {
      type: issue.startsWith('cheque') ? 'cheque' : 'carte',
      recu: !issue.startsWith('absent'),
      telecollecte: null, conforme: null, manquant: null, montant_reel: null,
    }
    switch (issue) {
      case 'carte_ok':   Object.assign(base, { telecollecte: true,  conforme: true,  manquant: false }); break
      case 'carte_ko':   Object.assign(base, { telecollecte: false, conforme: false, manquant: false, montant_reel: reel ?? 0 }); break
      case 'cheque_ok':  Object.assign(base, { conforme: true,  manquant: false }); break
      case 'cheque_ko':  Object.assign(base, { conforme: false, manquant: false, montant_reel: reel ?? 0 }); break
      case 'absent_ok':  Object.assign(base, { telecollecte: true,  conforme: true, manquant: true }); break
      case 'absent_ko':  Object.assign(base, { telecollecte: false, conforme: false, manquant: true }); break
    }

    const dossier = dossierDuJour(ligne)

    setOccupe(ligne.id)
    try {
      // L'ordre compte. Le dossier d'attente est la partie qui survit à la
      // journée : on l'ouvre AVANT d'écrire la vérification. Dans l'autre sens,
      // une coupure réseau laissait la ligne marquée « en attente » sans aucun
      // dossier derrière — l'affaire disparaissait le soir même.
      //
      // Un dossier par ligne, pas deux : repasser en attente une ligne qui en a
      // déjà un ouvert (après un aller-retour par « revenir sur l'issue ») en
      // empilait un second, et la carte du haut réclamait deux fois le même
      // justificatif.
      if (issue === 'absent_ko' && !dossier) {
        await padAjouter(ligne.ref, ligne.id, ligne.montant)
      }

      const map = await api.enregistrerPadVerif(
        date, ligne.id, base as unknown as Record<string, unknown>,
      )
      majRapport({ padVerifs: map })

      if (issue === 'absent_ko') {
        toast('PAD MIS EN ATTENTE — IL REVIENDRA DEMAIN')
      } else if (dossier) {
        // Changer d'issue referme le dossier que la ligne avait ouvert plus tôt
        // dans la journée. Sans cela il poursuivait les jours suivants alors
        // que plus rien à l'écran ne s'y rattachait.
        await padResoudre(dossier.id, 'resolu', `Issue rechangée sur ${ligne.ref}`)
        toast('DOSSIER D’ATTENTE REFERMÉ — L’ISSUE A CHANGÉ')
      }
      return true
    } catch (e) {
      toast(messageErreur(e), true)
      return false
    } finally {
      setOccupe(null)
    }
  }

  /**
   * Revenir sur une issue : la vérification est effacée, la ligne repart à
   * statuer — et le dossier d'attente qu'elle avait ouvert le jour même se
   * referme avec elle. Sans cela il survivait seul, sans plus rien à l'écran
   * pour s'y rattacher, exactement comme en changeant d'issue.
   *
   * L'ordre est l'inverse de `statuer`, pour le même invariant : jamais une
   * ligne marquée « EN ATTENTE » sans dossier derrière. On efface donc la
   * vérification d'abord ; une coupure au milieu laisse un dossier orphelin,
   * mais VISIBLE dans la carte du haut, où il se solde à la main.
   */
  const rouvrir = async (ligne: LignePad) => {
    if (lectureSeule || occupe) return
    const dossier = dossierDuJour(ligne)
    setOccupe(ligne.id)
    try {
      majRapport({ padVerifs: await api.enregistrerPadVerif(date, ligne.id, null) })
      if (dossier) {
        await padResoudre(dossier.id, 'resolu', `Issue rouverte sur ${ligne.ref}`)
        toast('DOSSIER D’ATTENTE REFERMÉ — LIGNE À STATUER À NOUVEAU')
      }
    } catch (e) {
      toast(messageErreur(e), true)
    } finally {
      setOccupe(null)
    }
  }

  const ouvrirSaisie = (ligne: LignePad, issue: Issue) => {
    // Repartir du montant déjà saisi s'il existe, du montant ORIKA sinon.
    const deja = montantReel(ligne)
    setMontant((deja ?? ligne.montant).toFixed(2).replace('.', ','))
    setSaisie({ ligne, issue })
  }

  const validerSaisie = async () => {
    if (!saisie || occupe) return
    const n = montantSaisi(montant)
    if (n === null) { toast('MONTANT REÇU INVALIDE — SAISIS UN NOMBRE POSITIF', true); return }
    if (await statuer(saisie.ligne, saisie.issue, n)) setSaisie(null)
  }

  /** Le justificatif est arrivé : le dossier est soldé, sans confirmation (réversible). */
  const solder = async (p: PadAttente) => {
    if (lectureSeule || occupe) return
    setOccupe(p.id)
    try {
      await padResoudre(p.id, 'resolu', 'Justificatif reçu')
      toast('DOSSIER SOLDÉ')
    } catch (e) {
      toast(messageErreur(e), true)
    } finally {
      setOccupe(null)
    }
  }

  /**
   * Renoncer : le dossier devient un écart définitif. Irréversible → confirmé,
   * et motivé. Le motif est la SEULE trace qui restera de la décision, alors il
   * est exigé : sans lui, Entrée dans la modale suffisait à effacer un dossier
   * pour toujours en laissant « sans motif » au journal.
   */
  const confirmerRenoncement = async () => {
    const p = renoncement
    if (!p || occupe || !motif.trim()) return
    setOccupe(p.id)
    try {
      await padResoudre(p.id, 'bascule_ecart', motif.trim())
      toast('PAD BASCULÉ EN ÉCART')
      setRenoncement(null)
    } catch (e) {
      toast(messageErreur(e), true)
    } finally {
      setOccupe(null)
    }
  }

  // Tant qu'aucune ligne n'est statuée, l'écart n'est pas nul : il est INCONNU.
  // L'annoncer « 0,00 € » en vert ferait passer un contrôle qui n'a pas eu lieu
  // pour une conformité.
  const ecartConnu = statues > 0

  const saisiApercu = saisie ? montantSaisi(montant) : null
  const ecartApercu = saisie && saisiApercu !== null
    ? +(saisiApercu - saisie.ligne.montant).toFixed(2)
    : null

  return (
    <>
      {/* Les dossiers en attente se voient TOUJOURS, même un jour sans PAD à
          l'import : ils viennent des journées précédentes et cet écran est le
          seul endroit où on peut les solder. */}
      {enAttente.length > 0 && (
        <Card
          titre={`PAD en attente — ${pluriel(enAttente.length, 'dossier ouvert', 'dossiers ouverts')}`}
          lampe="warn"
          style={{ marginBottom: 12 }}
          actions={<Chip>NE BLOQUE PAS LA VALIDATION DE L’ÉTAPE</Chip>}
        >
          {enAttente.map((p) => {
            const jours = joursOuvert(p.journee_origine)
            return (
              <div className="li" key={p.id}>
                <Lamp c="warn" />
                <div className="body">
                  <div className="t">
                    <span className="mo">{p.pad_ref}</span> · {fmtEur(Number(p.montant_attendu) || 0)}
                  </div>
                  <div className="s">
                    OUVERT LE {formatShort(p.journee_origine)} PAR {(p.cree_par_nom ?? '—').toUpperCase()}
                    {jours > 0 && ` · REPORTÉ DEPUIS ${pluriel(jours, 'JOUR', 'JOURS')}`}
                  </div>
                </div>
                {!lectureSeule && (
                  <>
                    <button className="btn ok sm" data-encours={encours}
                            onClick={() => void solder(p)}>
                      Enregistrer le justificatif reçu
                    </button>{' '}
                    <button className="btn stop sm" data-encours={encours}
                            onClick={() => { setMotif(''); setRenoncement(p) }}>
                      Basculer en écart
                    </button>
                  </>
                )}
              </div>
            )
          })}
          <div style={{ padding: '8px 12px', borderTop: '1px solid var(--line)' }} className="hint">
            Deux sorties, toutes deux volontaires : le justificatif arrive, ou tu renonces.
            Sans décision de ta part, le dossier reste visible tous les jours — et l’étape se
            valide quand même.
          </div>
        </Card>
      )}

      {lignes.length === 0 ? (
        <Vide>
          {enAttente.length > 0 ? (
            <>
              Aucun PAD dans l’import du jour. Les dossiers ci-dessus viennent des journées
              précédentes : solde ce qui peut l’être, puis valide l’étape.
            </>
          ) : (
            <>
              Aucun PAD dans l’import du jour — cette étape aurait dû être grisée par règle.
              Valide-la pour passer à la suite, ou reviens à l’étape 01 si l’import des
              règlements manque.
            </>
          )}
        </Vide>
      ) : (
        <>
          <Kpis items={[
            { k: 'PAD statués', v: `${statues} / ${lignes.length}` },
            { k: 'Montant ORIKA', v: fmtEur(total) },
            {
              k: 'Montant retenu', v: fmtEur(total + ecartTotal),
              s: 'ce qui part en COL MNC',
            },
            {
              k: 'Écart déjà expliqué',
              v: ecartConnu ? fmtEcart(ecartTotal) : '—',
              s: ecartConnu ? motEcart(ecartTotal) : 'aucune ligne statuée',
              couleur: ecartConnu ? couleurEcart(ecartTotal) : 'var(--ink4)',
            },
            {
              k: 'En attente', v: enAttente.length,
              couleur: enAttente.length ? 'var(--warn)' : undefined,
            },
          ]} />

          {ecartConnu && severite(ecartTotal) !== 'nul' && (
            <Notice c={lampeEcart(ecartTotal)}>
              <b>Voici ce qui explique déjà {fmtEcart(ecartTotal)} de ton écart de flux.</b>{' '}
              Les divergences PAD ci-dessous partent dans la justification de l’écart — tu ne repars
              pas d’un champ vide.
            </Notice>
          )}

          {/* La lampe de la carte ne dit « vert » que si TOUT est statué ET que
              l'écart retenu est nul. « Tout est statué » seul allumait un vert
              au-dessus d'un tableau à −45 € : la complétude du contrôle se
              lisait comme une conformité. La complétude, elle, est déjà dite
              par le KPI « PAD statués ». */}
          <Card titre="Les PAD du jour"
                lampe={statues < lignes.length ? 'warn' : lampeEcart(ecartTotal)}>
            <div className="scrollx">
              <table className="t">
                <thead>
                  <tr>
                    <th>Référence</th><th>Caisse / ticket</th><th className="n">Montant ORIKA</th>
                    <th>Issue</th><th className="n">Montant retenu</th><th className="n">Écart</th>
                    <th style={{ minWidth: 300 }} />
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((l) => (
                    <LignePadRow key={l.id} l={l} lectureSeule={lectureSeule}
                                 occupe={busy}
                                 statuer={statuer} ouvrirSaisie={ouvrirSaisie}
                                 rouvrir={() => void rouvrir(l)} />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {saisie && (
        <Modale
          titre="Montant réellement reçu"
          sous={`${saisie.ligne.ref} — ORIKA annonce ${fmtEur(saisie.ligne.montant)}. Saisis ce qui est réellement entré : c'est ce montant qui partira dans les totaux COL MNC.`}
          fermer={() => setSaisie(null)}
        >
          <label className="lbl" htmlFor="mnt">Montant reçu (€)</label>
          {/* Le champ arrive prérempli ET sélectionné : taper remplace, sans
              passer par un Ctrl+A ni un effacement au clavier. Un préremplissage
              avec le caret en bout de ligne donnait « 12,4012,40 ». */}
          <input id="mnt" className="mo" inputMode="decimal" value={montant} autoFocus
                 onFocus={(e) => e.currentTarget.select()}
                 onChange={(e) => setMontant(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void validerSaisie() } }} />
          <div className="hint" style={{ marginBottom: 10 }}>
            {ecartApercu === null ? (
              'Saisis le montant pour voir l’écart.'
            ) : (
              <>
                Écart calculé :{' '}
                <span className="mo" style={{ color: couleurEcart(ecartApercu) }}>
                  {fmtEcart(ecartApercu)}
                </span>{' '}
                — {motEcart(ecartApercu)}
              </>
            )}
          </div>
          <div className="btns">
            <button className="btn" onClick={() => setSaisie(null)}>Annuler</button>
            <button className="btn pri" data-encours={encours}
                    onClick={() => void validerSaisie()}>
              Enregistrer l’anomalie
            </button>
          </div>
        </Modale>
      )}

      {renoncement && (
        <Modale
          titre="Basculer ce PAD en écart"
          sous={`${renoncement.pad_ref} — ${fmtEur(Number(renoncement.montant_attendu) || 0)}, ouvert le ${formatShort(renoncement.journee_origine)}. Tu renonces au justificatif : le dossier se ferme définitivement et le montant reste un écart. Il ne reviendra plus.`}
          fermer={() => setRenoncement(null)}
        >
          <label className="lbl" htmlFor="pad-motif">Motif du renoncement</label>
          <input id="pad-motif" value={motif} autoFocus
                 placeholder="Ex. : justificatif introuvable après relance de la banque"
                 onChange={(e) => setMotif(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void confirmerRenoncement() } }} />
          <div className="hint" style={{ margin: '6px 0 10px' }}>
            Le motif part au journal : c’est la seule trace qui restera de la décision,
            et il est obligatoire pour cette raison.
          </div>
          <div className="btns">
            <button className="btn" onClick={() => setRenoncement(null)}>Renoncer à basculer</button>
            <button className="btn stop" data-encours={encours} disabled={!motif.trim()}
                    onClick={() => void confirmerRenoncement()}>
              Basculer en écart
            </button>
          </div>
        </Modale>
      )}
    </>
  )
}

function LignePadRow({ l, lectureSeule, occupe, statuer, ouvrirSaisie, rouvrir }: {
  l: LignePad
  lectureSeule: boolean
  /**
   * Une écriture est en vol QUELQUE PART dans l'écran — pas forcément sur cette
   * ligne. Les écritures sont sérialisées (chaque appel renvoie la table des
   * vérifications entière, deux réponses concurrentes s'écrasent), donc un clic
   * sur une autre ligne serait de toute façon refusé : autant qu'il se voie.
   * Marquer la seule ligne occupée laissait les autres boutons vifs à l'œil et
   * inertes au clic — le pire des deux.
   */
  occupe: boolean
  statuer: (ligne: LignePad, issue: Issue) => Promise<boolean>
  ouvrirSaisie: (ligne: LignePad, issue: Issue) => void
  rouvrir: () => void
}) {
  const [branche, setBranche] = useState<'A' | 'B' | null>(null)
  const reel = montantReel(l)
  const encours = occupe ? 'true' : undefined

  return (
    <tr>
      <td className="mo" style={{ fontSize: 11.5 }}>{l.ref}</td>
      <td className="mo" style={{ fontSize: 11, color: 'var(--ink3)' }}>{l.caisse} / {l.ticket}</td>
      <td className="n">{fmtEur(l.montant)}</td>
      <td>{etiquette(l)}</td>
      {/* Le montant retenu se lit dès qu'il a été saisi — y compris quand il est
          égal au montant ORIKA. L'écran testait l'écart et non sa présence :
          une anomalie au bon montant s'affichait « — », comme jamais saisie. */}
      <td className="n" style={reel === null ? { color: 'var(--ink4)' } : undefined}>
        {reel === null ? '—' : fmtEur(reel)}
      </td>
      <td className="n"
          style={{ color: reel === null ? 'var(--ink4)' : couleurEcart(l.ecart) }}
          title={reel === null ? undefined : motEcart(l.ecart)}>
        {reel === null ? '—' : fmtEcart(l.ecart)}
      </td>
      <td>
        {lectureSeule ? null : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, justifyContent: 'flex-end' }}>
            {l.statue ? (
              <button className="btn gh sm" data-encours={encours}
                      // La branche ouverte est un état d'écran, pas une donnée. Sans
                      // cette remise à zéro, rouvrir une ligne la relançait au milieu
                      // de la branche choisie trois clics plus tôt.
                      onClick={() => { setBranche(null); rouvrir() }}>
                Revenir sur l’issue
              </button>
            ) : (
              <>
                {/* Choisir une branche ne parle pas au serveur : ces deux-là
                    restent vivants pendant qu'une autre ligne s'enregistre. On
                    prépare le clic suivant au lieu d'attendre le réseau. */}
                <button className="btn sm" aria-pressed={branche === 'A'}
                        onClick={() => setBranche((b) => (b === 'A' ? null : 'A'))}>
                  J’ai le justificatif
                </button>
                <button className="btn sm" aria-pressed={branche === 'B'}
                        onClick={() => setBranche((b) => (b === 'B' ? null : 'B'))}>
                  Je ne l’ai pas
                </button>

                {branche === 'A' && (
                  <>
                    <button className="btn ok sm" data-encours={encours}
                            onClick={() => void statuer(l, 'carte_ok')}>
                      Carte télécollectée
                    </button>
                    <button className="btn warn sm" data-encours={encours}
                            onClick={() => ouvrirSaisie(l, 'carte_ko')}>
                      Carte non télécollectée…
                    </button>
                    <button className="btn ok sm" data-encours={encours}
                            onClick={() => void statuer(l, 'cheque_ok')}>
                      Chèque conforme
                    </button>
                    <button className="btn warn sm" data-encours={encours}
                            onClick={() => ouvrirSaisie(l, 'cheque_ko')}>
                      Chèque au mauvais montant…
                    </button>
                  </>
                )}

                {branche === 'B' && (
                  <>
                    <button className="btn ok sm" data-encours={encours}
                            onClick={() => void statuer(l, 'absent_ok')}>
                      Carte télécollectée — clore le dossier
                    </button>
                    {/* Mettre en attente n'est pas destructif : le dossier se solde
                        ou se bascule plus tard. Ton « différer », pas « stop ». */}
                    <button className="btn warn sm" data-encours={encours}
                            onClick={() => void statuer(l, 'absent_ko')}>
                      Non télécollectée — mettre en attente
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </td>
    </tr>
  )
}

/**
 * L'étiquette d'issue.
 *
 * Le vert n'y est jamais accordé à une ligne qui porte un écart. Aujourd'hui la
 * question ne se pose pas — les trois issues conformes n'écrivent pas de montant
 * réel, donc leur écart est nul par construction — mais cette garantie vit dans
 * `statuer`, à deux cents lignes d'ici, et la vérification affichée peut aussi
 * venir d'une reprise V2 ou d'une écriture partielle. La règle « un écart n'est
 * jamais vert » se vérifie donc là où la couleur se pose, plutôt que de se
 * déduire de la bonne conduite de celui qui a écrit.
 */
function etiquette(l: LignePad) {
  if (!l.statue) return <Chip c="warn">À STATUER</Chip>
  const v = l.verif
  const conforme = (texte: string) =>
    severite(l.ecart) === 'nul'
      ? <Chip c="ok">{texte}</Chip>
      : <Chip c="warn">{texte} · {fmtEcart(l.ecart)}</Chip>

  if (v?.recu === false) {
    return v.telecollecte
      ? conforme('SANS JUSTIF · TÉLÉCOLLECTÉE')
      : <Chip c="stop">EN ATTENTE</Chip>
  }
  if (v?.type === 'carte') {
    return v.telecollecte ? conforme('CARTE CONFORME') : <Chip c="warn">CARTE NON TÉLÉCOLLECTÉE</Chip>
  }
  return v?.conforme ? conforme('CHÈQUE CONFORME') : <Chip c="warn">CHÈQUE NON CONFORME</Chip>
}
